import { Hub } from 'aws-amplify/utils'
import { useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useSetAgentId } from '~/features/agents/hooks/useSetAgentId'
import useCompleteSignIn from '~/features/auth/hooks/useCompleteSignIn'
import useSignOut, {
  isHardSignOutInFlight,
} from '~/features/auth/hooks/useSignOut'
import useSignoutContext from '~/features/auth/hooks/useSignoutContext'
import { TSessionDetails } from '~/features/auth/models'
import { isDeadSessionError } from '~/features/auth/utils'
import useValidateUser from '~/features/user/hooks/useValidateUser'
import useGetAuthSessionQuery from '~/hooks/useGetAuthSessionQuery'
import { EAdminPortalClientPath, EClientPath } from '~/models/api'
import { ECognitoGroups, TCustomOAuthState } from '~/models/auth'
import { store } from '~/store'

const useAuthListener = () => {
  const { mutateAsync: validateUser } = useValidateUser()
  const setAgentId = useSetAgentId()
  const [lastSelectedAgentId, setUser, signInRedirectPath] = store((state) => [
    state.user.lastSelectedAgentId,
    state.setUser,
    state.user.signInRedirectPath,
  ])

  const navigate = useNavigate()
  const { pathname } = useLocation()

  const { completeSignIn } = useCompleteSignIn()
  const { refetch } = useGetAuthSessionQuery(false)
  const { signOut } = useSignOut()

  const { isSigningOut, setIsSigningOut } = useSignoutContext()

  const onSignOut = useCallback(() => {
    if (isSigningOut) return

    setIsSigningOut(true)
  }, [isSigningOut, setIsSigningOut])

  const handleUserValidation = useCallback(async () => {
    const { data: authSession } = await refetch()

    try {
      await validateUser({
        email: authSession?.email || '',
        cognitoId: authSession?.cognitoUid || '',
      })
      setUser({ isValidated: true })
      return authSession
    } catch {
      // if validation fails, sign out the user to prevent them from being stuck in a broken state
      throw new Error('User validation failed')
    }
  }, [refetch, validateUser, setUser])

  const handleNavigation = useCallback(
    async (authSession: TSessionDetails | undefined) => {
      // completeSignIn throws on failure (after toast). Skip navigation when
      // session is missing so we don't enter a protected route during logout.
      if (!authSession?.cognitoUid) return authSession

      if (signInRedirectPath) {
        navigate(signInRedirectPath)
        setUser({ signInRedirectPath: null })

        return authSession
      }

      if (authSession?.groups?.includes(ECognitoGroups.admin)) {
        navigate(EAdminPortalClientPath.admin)
      } else {
        navigate(EClientPath.overview, {
          state: { previousPathname: pathname },
        })
      }
      return authSession
    },
    [navigate, pathname, setUser, signInRedirectPath],
  )

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'customOAuthState':
          if (payload.data) {
            const customState = JSON.parse(payload.data) as TCustomOAuthState
            if (customState.signInRedirectPath) {
              setUser({ signInRedirectPath: customState.signInRedirectPath })
            }
          }
          break
        case 'signedIn':
          handleUserValidation()
            .then((authSession) => completeSignIn(authSession))
            .then((authSession) => handleNavigation(authSession))
            .then(() => {
              if (lastSelectedAgentId) {
                setAgentId({ agentId: lastSelectedAgentId })
              }
            })
            .catch(async () => {
              // Module latch — not React state — so concurrent fail paths join
              // one hard logout instead of flashing a second spinner on login.
              if (isHardSignOutInFlight()) return
              await signOut({
                navigateState: { validatedUserErrorMessage: true },
              })
            })
          break
        case 'signedOut':
          // Cognito cleared; ensure app clear runs. Navigation is owned by
          // the canonical hard logout that initiated sign-out when present.
          onSignOut()
          break
        case 'tokenRefresh_failure':
          if (isDeadSessionError(payload.data)) {
            await signOut()
          }
          break
        default:
          break
      }
    })

    return unsubscribe
  }, [
    completeSignIn,
    handleNavigation,
    lastSelectedAgentId,
    onSignOut,
    setAgentId,
    setUser,
    handleUserValidation,
    signOut,
  ])

  // Hub is same-tab only. Reload when another tab sets or clears
  // LastAuthUser. Ignore identityId — guest writes on login reload
  // the other tab in a loop.
  useEffect(() => {
    const handleAuthStorage = (event: StorageEvent) => {
      if (event.key?.endsWith('.LastAuthUser')) {
        window.location.reload()
      }
    }

    window.addEventListener('storage', handleAuthStorage)

    return () => {
      window.removeEventListener('storage', handleAuthStorage)
    }
  }, [])
}

export default useAuthListener
